import React, { useState } from "react";
import { Link } from "react-router";
import { ButtonBase, Menu } from "@sscs/ui";
import { ArrowDropDownIcon } from "@sscs/ui/icons";
import {
  AI_CONFIGURATION_ROUTE,
  AUDIT_ROUTE,
  CLI_CAPACITY_ROUTE,
  LICENSING_ROUTE,
  MEMBERS_ROUTE,
  PERMISSION,
  POLICY_CONFIG_ROUTE,
  SESSION_CONFIGURATION_ROUTE,
  SETTINGS_ROUTE,
  USER_DIRECTORY_ROUTE,
} from "Constants";
import { useAllowedTo } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import styles from "./organization.module.scss";

type OrganizationMenuItem = {
  id: string;
  to: string;
  label: string;
};

type OrganizationMenuGroup = {
  heading: string;
  items: OrganizationMenuItem[];
};

const Organization = (): React.JSX.Element => {
  const { data: whoami } = useGetWhoamiQuery();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const canManageMembers = useAllowedTo({
    permissions: [PERMISSION.MODIFY_MEMBERS_ORGANIZATION_ROLE],
  });
  const canViewMembers = useAllowedTo({
    permissions: [PERMISSION.VIEW_ALL_ORGANIZATION_MEMBERS],
  });
  const canManagePolicies = useAllowedTo({
    permissions: [PERMISSION.CONFIGURE_ORGANIZATION_POLICY_PROFILE],
  });
  const canManagePortalCapacity = useAllowedTo({
    permissions: [PERMISSION.REALLOCATE_QUOTAS],
  });
  const canViewCliCapacity = useAllowedTo({
    permissions: [PERMISSION.VIEW_CLI_SITE_KEY_ALLOCATIONS],
  });
  const canManageCliCapacity = useAllowedTo({
    permissions: [PERMISSION.MODIFY_CLI_SITE_KEY_ALLOCATIONS],
  });
  // mirrors the Licensing page gate, which cannot use
  // VIEW_ORGANIZATION_MONTHLY_CAPACITY_AND_USAGE because trial members hold it
  const canViewAnalysisCapacity = useAllowedTo({
    permissions: [PERMISSION.VIEW_MONTHLY_USAGE_PER_GROUP],
  });
  const canManageSso = useAllowedTo({
    permissions: [PERMISSION.SETUP_SSO],
  });
  const canConfigureSession = useAllowedTo({
    permissions: [PERMISSION.MODIFY_SESSION_CONFIGURATION],
  });
  const canConfigureAi = useAllowedTo({
    permissions: [PERMISSION.MODIFY_AI_CONFIGURATION],
  });
  const canViewAuditLog = useAllowedTo({
    permissions: [PERMISSION.VIEW_AUDIT],
  });

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const organizationBase = `/organization/${whoami?.organization_id ?? ""}`;

  const accessItems: OrganizationMenuItem[] = [];
  if (canViewMembers) {
    accessItems.push({
      id: "organization-members",
      to: `${organizationBase}/${MEMBERS_ROUTE}`,
      label: `${canManageMembers ? "Manage" : "View"} groups/members`,
    });
  }
  if (canManageSso) {
    accessItems.push({
      id: "organization-sso-configuration",
      to: `/${SETTINGS_ROUTE}/${USER_DIRECTORY_ROUTE}`,
      label: "Manage SSO configuration",
    });
  }
  if (canConfigureSession) {
    accessItems.push({
      id: "organization-session-lifetime",
      to: `${organizationBase}/${SESSION_CONFIGURATION_ROUTE}`,
      label: "Manage session lifetime",
    });
  }

  const usageItems: OrganizationMenuItem[] = [];
  if (canViewAnalysisCapacity) {
    usageItems.push({
      id: "organization-analysis-capacity",
      to: `${organizationBase}/${LICENSING_ROUTE}`,
      label: `${canManagePortalCapacity ? "Manage" : "Review"} portal capacity`,
    });
  }
  if (canViewCliCapacity) {
    usageItems.push({
      id: "organization-cli-allocations",
      to: `${organizationBase}/${CLI_CAPACITY_ROUTE}`,
      label: `${canManageCliCapacity ? "Manage" : "Review"} CLI capacity`,
    });
  }

  const platformItems: OrganizationMenuItem[] = [];
  if (canConfigureAi) {
    platformItems.push({
      id: "organization-ai-configuration",
      to: `${organizationBase}/${AI_CONFIGURATION_ROUTE}`,
      label: "Manage AI settings",
    });
  }
  if (canViewAuditLog) {
    platformItems.push({
      id: "organization-audit-log",
      to: `${organizationBase}/${AUDIT_ROUTE}`,
      label: "View audit logs",
    });
  }

  const groups: OrganizationMenuGroup[] = [
    { heading: "Access & identity", items: accessItems },
    { heading: "Usage & capacity", items: usageItems },
    {
      heading: "Security",
      items: [
        {
          id: "organization-policies",
          to: `${organizationBase}/${POLICY_CONFIG_ROUTE}`,
          label: `${canManagePolicies ? "Manage" : "View"} policies`,
        },
      ],
    },
    { heading: "Platform", items: platformItems },
  ].filter((group) => group.items.length > 0);

  return (
    <div id="organization-pages" className={styles.organization}>
      <ButtonBase
        className={styles.button}
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          handleOpenMenuClick(event);
        }}
      >
        <span>Organization</span>
        <ArrowDropDownIcon className={styles.icon} />
      </ButtonBase>

      <Menu.Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        onClick={handleMenuClose}
        keepMounted
        anchorOrigin={{
          vertical: 30,
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {groups.flatMap((group) => [
          <Menu.GroupTitle key={group.heading}>
            {group.heading}
          </Menu.GroupTitle>,
          ...group.items.map((item) => (
            <Menu.Item key={item.id} id={item.id} component={Link} to={item.to}>
              {item.label}
            </Menu.Item>
          )),
        ])}
      </Menu.Menu>
    </div>
  );
};

export default Organization;
